import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TemplateEditorModal } from '@/components/notification-templates/TemplateEditorModal';
import { TemplateTable } from '@/components/notification-templates/TemplateTable';
import { listTemplates } from '@/services/notificationTemplateService';

import type { NotificationTemplate } from '@/types/notificationTemplate.types';

const EVENT_TYPES = ['auth', 'document', 'booking', 'notification', 'jobPosting'] as const;
const CHANNELS = ['email', 'sms'] as const;

export function NotificationTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

  const [filterEventType, setFilterEventType] = useState('');
  const [filterChannel, setFilterChannel] = useState('');

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listTemplates({
        eventType: filterEventType || undefined,
        channel: filterChannel || undefined,
      });
      setTemplates(data);
    } catch {
      setError(t('notificationTemplates.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [filterEventType, filterChannel, t]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleSaved = useCallback(() => {
    setEditingTemplate(null);
    void loadTemplates();
  }, [loadTemplates]);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-bold text-gray-900'>{t('notificationTemplates.title')}</h1>
        <p className='mt-1 text-sm text-gray-500'>{t('notificationTemplates.description')}</p>
      </div>

      {/* Filters */}
      <div className='flex gap-3'>
        <select
          value={filterEventType}
          onChange={(e) => setFilterEventType(e.target.value)}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'>
          <option value=''>{t('notificationTemplates.filters.allEventTypes')}</option>
          {EVENT_TYPES.map((et) => (
            <option key={et} value={et}>
              {t(`notificationTemplates.filters.${et}`)}
            </option>
          ))}
        </select>
        <select
          value={filterChannel}
          onChange={(e) => setFilterChannel(e.target.value)}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'>
          <option value=''>{t('notificationTemplates.filters.allChannels')}</option>
          {CHANNELS.map((ch) => (
            <option key={ch} value={ch}>
              {ch.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>{error}</div>}

      {/* Loading */}
      {isLoading && !error && (
        <div className='flex justify-center py-12'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && templates.length === 0 && (
        <div className='rounded-lg border border-gray-200 bg-white p-12 text-center'>
          <p className='text-sm text-gray-500'>{t('notificationTemplates.empty')}</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && templates.length > 0 && (
        <TemplateTable templates={templates} onEdit={setEditingTemplate} />
      )}

      {/* Editor Modal */}
      <TemplateEditorModal template={editingTemplate} onClose={() => setEditingTemplate(null)} onSaved={handleSaved} />
    </div>
  );
}
